import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table, Row, Col } from 'react-bootstrap';
import { FaCalculator, FaLock, FaTimes, FaChartBar } from 'react-icons/fa';
import api from '../services/api';

const RestructuringModal = ({ show, onHide, report, onUpdate }) => {
    const [step, setStep] = useState('input'); // 'input', 'result', 'confirmed'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Input state
    const [newInterestRate, setNewInterestRate] = useState(7.0);
    const [newTerm, setNewTerm] = useState(12);

    // Calculation result state
    const [calculationData, setCalculationData] = useState(null);

    // Confirmation state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [adminPassword, setAdminPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmationLoading, setConfirmationLoading] = useState(false);

    // Modal açıldığında state'i sıfırla
    useEffect(() => {
        if (show && report) {
            setStep('input');
            setNewInterestRate(report.financialData?.interestRate || 7.0); // ← DEĞİŞTİ
            setNewTerm(12);
            setCalculationData(null);
            setError('');
            setShowPasswordModal(false);
            setAdminPassword('');
            setPasswordError('');
        }
    }, [show, report]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    // ✅ YENİ: Kalan Ana Para Hesapla (EarlySettlementModal ile aynı)
    const calculateRemainingPrincipal = () => {
        if (!report || !report.financialData) {
            return 0;
        }

        const loanAmount = report.financialData.loanAmount || 0;
        const monthlyRate = (report.financialData.interestRate || 0) / 100;
        const term = report.financialData.term || 1;
        const monthlyPayment = report.financialData.monthlyPayment || 0;

        const paidCount = report.paymentPlan
            ? report.paymentPlan.filter(p => p.paymentStatus === 'paid' && !p.isRestructured).length
            : 0;

        if (paidCount === 0) {
            return loanAmount;
        }

        let remainingPrincipal = loanAmount;

        for (let i = 0; i < paidCount; i++) {
            const interest = remainingPrincipal * monthlyRate;
            const principal = monthlyPayment - interest;
            remainingPrincipal -= principal;
        }

        return Math.max(0, remainingPrincipal);
    };

    // ✅ BANKA STANDARDI: Son ödeme tarihi ile bugün arası gün farkı
    const calculateDaysSinceLastPayment = () => {
        if (!report || !report.paymentPlan) return 0;

        const paidInstallments = report.paymentPlan
            .filter(p => p.paymentStatus === 'paid' && !p.isRestructured)
            .sort((a, b) => new Date(b.taksitTarihi) - new Date(a.taksitTarihi));

        if (paidInstallments.length === 0) return 0;

        const lastPaymentDate = new Date(paidInstallments[0].taksitTarihi);
        lastPaymentDate.setHours(0, 0, 0, 0); // Saat sıfırla

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Saat sıfırla

        const diffTime = today - lastPaymentDate; // Milisaniye farkı
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Tam gün

        return diffDays; // Başlangıç günü hariç
    };

    // Hesaplama fonksiyonu
    const handleCalculate = async () => {
        try {
            setLoading(true);
            setError('');

            // Validation
            if (newInterestRate <= 0 || newInterestRate > 100) {
                setError('Faiz oranı 0-100 arasında olmalıdır');
                setLoading(false);
                return;
            }

            if (newTerm < 1 || newTerm > 120) {
                setError('Vade 1-120 ay arasında olmalıdır');
                setLoading(false);
                return;
            }

            const response = await api.get(
                `/restructuring/calculate/${report._id}?newInterestRate=${newInterestRate}&newTerm=${newTerm}`
            );

            if (response.data.success) {
                setCalculationData(response.data.calculation);
                setStep('result');
            }
        } catch (error) {
            console.error('Hesaplama hatası:', error);
            setError(error.response?.data?.message || 'Hesaplama sırasında hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Onay isteği (şifre modal aç)
    const handleConfirmRequest = () => {
        setShowPasswordModal(true);
        setPasswordError('');
        setAdminPassword('');
    };

    // Onaylama fonksiyonu
    const handleConfirm = async () => {
        try {
            setConfirmationLoading(true);
            setPasswordError('');

            // Şifre kontrolü
            if (adminPassword !== '123456') {
                setPasswordError('Yanlış admin şifresi!');
                return;
            }

            const response = await api.post(`/restructuring/confirm/${report._id}`, {
                calculationData: calculationData,
                adminPassword: adminPassword
            });

            if (response.data.success) {
                setStep('confirmed');
                setShowPasswordModal(false);
                onUpdate && onUpdate();
            }
        } catch (error) {
            console.error('Onaylama hatası:', error);
            setPasswordError(error.response?.data?.message || 'Onaylama sırasında hata oluştu');
        } finally {
            setConfirmationLoading(false);
        }
    };

    const handleCancelPasswordModal = () => {
        setShowPasswordModal(false);
        setAdminPassword('');
        setPasswordError('');
    };

    const getModalTitle = () => {
        switch (step) {
            case 'input':
                return (
                    <>
                        <FaCalculator className="text-warning me-2" />
                        Yapılandırma Hesaplama - {report?.customerData?.name}
                    </>
                );
            case 'result':
                return (
                    <>
                        <FaChartBar className="text-info me-2" />
                        Yapılandırma Sonuçları - {report?.customerData?.name}
                    </>
                );
            case 'confirmed':
                return (
                    <>
                        <FaLock className="text-success me-2" />
                        Yapılandırma Tamamlandı - {report?.customerData?.name}
                    </>
                );
            default:
                return 'Yapılandırma İşlemi';
        }
    };

    if (!report) return null;

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{getModalTitle()}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {/* ADIM 1: GİRDİ EKRANI */}
                    {step === 'input' && (
                        <>
                            {/* MEVCUT FİNANSMAN BİLGİLERİ - GÜNCELLENDİ */}
                            <Alert variant="info" className="mb-4">
                                <strong>Mevcut Finansman Bilgileri:</strong>
                                <Row className="mt-2">
                                    <Col md={6}>
                                        <small>Kredi Tutarı: {formatCurrency(report.financialData?.loanAmount || 0)}</small>
                                    </Col>
                                    <Col md={6}>
                                        <small>Aylık Taksit: {formatCurrency(report.financialData?.monthlyPayment || 0)}</small>
                                    </Col>
                                    <Col md={6}>
                                        <small>Toplam Ödeme: {formatCurrency(report.financialData?.totalPayment || 0)}</small>
                                    </Col>
                                    <Col md={6}>
                                        <small>Kalan Ana Para: {formatCurrency(calculateRemainingPrincipal())}</small>
                                    </Col>
                                    <Col md={6}>
                                        <small>Vade: {report.financialData?.term} ay</small>
                                    </Col>
                                    <Col md={6}>
                                        <small>Faiz Oranı: %{report.financialData?.interestRate}</small>
                                    </Col>
                                </Row>
                            </Alert>

                            {/* GECİKME FAİZİ HESAPLAMA */}
                            <Alert variant="warning" className="mb-4">
                                <div className="mb-2">
                                    <strong>Gecikme Günü:</strong> {calculateDaysSinceLastPayment()} gün
                                </div>
                                <div className="p-3 bg-white rounded border">
                                    <small className="d-block mb-2">
                                        <strong>Yapılandırma Tutarı Hesaplama Formülü:</strong>
                                    </small>
                                    <code className="d-block text-dark">
                                        Yapılandırma Tutarı = Ana Para + (Ana Para × Gecikme Günü × Faiz / 30)
                                    </code>
                                    <hr className="my-2" />
                                    <small className="d-block text-muted">
                                        = {formatCurrency(calculateRemainingPrincipal())} +
                                        ({formatCurrency(calculateRemainingPrincipal())} × {calculateDaysSinceLastPayment()} × {report.financialData?.interestRate}% / 30)
                                    </small>
                                    <small className="d-block mt-1">
                                        <strong>= {formatCurrency(
                                            calculateRemainingPrincipal() +
                                            (calculateRemainingPrincipal() * calculateDaysSinceLastPayment() * (report.financialData?.interestRate || 0) / 100 / 30)
                                        )}</strong>
                                    </small>
                                </div>
                            </Alert>

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label><strong>Yeni Aylık Faiz Oranı (%) *</strong></Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            max="100"
                                            value={newInterestRate}
                                            onChange={(e) => setNewInterestRate(parseFloat(e.target.value))}
                                            placeholder="7.5"
                                            required
                                        />
                                        <span className="input-group-text">%</span>
                                    </div>
                                    <Form.Text className="text-muted">
                                        Yeni yapılandırma için uygulanacak faiz oranı
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label><strong>Yeni Vade Süresi (Ay) *</strong></Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="120"
                                        value={newTerm}
                                        onChange={(e) => setNewTerm(parseInt(e.target.value))}
                                        placeholder="12"
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        Yeni yapılandırma kaç ay olacak (1-120 ay)
                                    </Form.Text>
                                </Form.Group>
                            </Form>

                            <div className="text-center mt-4">
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={handleCalculate}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Hesaplanıyor...
                                        </>
                                    ) : (
                                        <>
                                            <FaCalculator className="me-2" />
                                            Hesapla
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}

                    {/* ADIM 2: SONUÇ EKRANI */}
                    {step === 'result' && calculationData && (
                        <>
                            {/* Özet Bilgiler */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="card border-primary">
                                        <div className="card-body text-center">
                                            <h6 className="text-muted">Ana Para (Kalan Borç)</h6>
                                            <h4 className="text-primary">{formatCurrency(calculationData.originalDebt)}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="card border-success">
                                        <div className="card-body text-center">
                                            <h6 className="text-muted">Yeni Aylık Ödeme</h6>
                                            <h4 className="text-success">{formatCurrency(calculationData.newTerms.monthlyPayment)}</h4>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Finansal Detaylar */}
                            <div className="card mb-4">
                                <div className="card-header bg-info text-white">
                                    <strong>Yeni Finansman Koşulları</strong>
                                </div>
                                <div className="card-body">
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Faiz Oranı:</strong> %{calculationData.newTerms.interestRate}</p>
                                            <p><strong>Vade:</strong> {calculationData.newTerms.term} ay</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Toplam Ödeme:</strong> {formatCurrency(calculationData.newTerms.totalPayment)}</p>
                                            <p><strong>Toplam Faiz:</strong> {formatCurrency(calculationData.newTerms.totalInterest)}</p>
                                        </Col>
                                    </Row>
                                </div>
                            </div>

                            {/* Yapılandırılacak Taksitler */}
                            <Alert variant="warning" className="mb-3">
                                <strong>Yapılandırılacak Taksitler:</strong> {calculationData.oldInstallmentRange.start}. taksit - {calculationData.oldInstallmentRange.end}. taksit
                                <br />
                                <small>Son ödenen: {calculationData.lastPaidInstallment.number}. taksit ({formatDate(calculationData.lastPaidInstallment.date)})</small>
                            </Alert>

                            {/* Yeni Taksit Listesi */}
                            <div className="card">
                                <div className="card-header bg-success text-white">
                                    <strong>Yeni Taksit Planı ({calculationData.newPaymentPlan.length} adet)</strong>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <Table striped bordered hover className="mb-0">
                                            <thead className="sticky-top bg-white">
                                                <tr>
                                                    <th>Taksit No</th>
                                                    <th>Tarih</th>
                                                    <th>Tutar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {calculationData.newPaymentPlan.map((installment) => (
                                                    <tr key={installment.taksitNo}>
                                                        <td>{installment.taksitNo}</td>
                                                        <td>{formatDate(installment.taksitTarihi)}</td>
                                                        <td>{formatCurrency(installment.taksitTutari)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ADIM 3: ONAY EKRANI */}
                    {step === 'confirmed' && (
                        <Alert variant="success" className="text-center">
                            <h4>Yapılandırma Tamamlandı!</h4>
                            <p>İşlem başarıyla kaydedildi. Eski taksitler "Yapılandırıldı" olarak işaretlendi ve yeni taksitler eklendi.</p>
                        </Alert>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        <FaTimes className="me-2" />
                        Kapat
                    </Button>

                    {step === 'result' && (
                        <Button
                            variant="success"
                            onClick={handleConfirmRequest}
                            disabled={loading}
                        >
                            <FaLock className="me-2" />
                            Onayla ve Kaydet
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* ŞİFRE ONAY MODAL */}
            <Modal show={showPasswordModal} onHide={handleCancelPasswordModal} size="sm" centered>
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title>
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        GÜVENLİK ONAYI
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="alert alert-danger">
                        <strong>DİKKAT!</strong><br />
                        Bu işlem <strong>GERİ ALINAMAZ</strong>!<br />
                        Eski taksitler yapılandırılacak ve yeni taksitler eklenecek.
                    </div>

                    {calculationData && (
                        <h5 className="text-dark mb-3">
                            Yeni Aylık Ödeme: <span className="text-success">{formatCurrency(calculationData.newTerms.monthlyPayment)}</span>
                        </h5>
                    )}

                    <div className="mb-3">
                        <label className="form-label"><strong>Admin Şifrenizi Girin:</strong></label>
                        <input
                            type="password"
                            className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                            placeholder="Admin şifresi"
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
                            autoFocus
                        />
                        {passwordError && (
                            <div className="invalid-feedback">
                                {passwordError}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelPasswordModal}>
                        <FaTimes className="me-2" />
                        İptal
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirm}
                        disabled={!adminPassword || confirmationLoading}
                    >
                        {confirmationLoading ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Onaylanıyor...
                            </>
                        ) : (
                            <>
                                <FaLock className="me-2" />
                                EVET, ONAYLA
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RestructuringModal;